import { GET_ORDER_GUIDE_PRICE_EDIT_DISPLAY_INFO } from '@/components/orders/hooks/use-get-price-edit-display-info';
import {
  OfflineBackgroundSyncRegistryAdapter,
  OfflineOrderGuidePriceEditDisplayInfoAdapter,
} from '@/adapters/offline-mode';
import { OfflineSyncType } from '@/utils/offline-mode';
import {
  PriceEditing_GetPriceEditDisplayInfoQuery,
  PriceEditing_GetPriceEditDisplayInfoQueryVariables,
} from '@/types';
import { print as printQuery } from 'graphql';
import { useLazyQuery } from '@/hooks/use-lazy-query';

/**
 * Custom hook to download and save the price edit config for a specific restaurant and supplier.
 *
 * This hook uses a lazy query to fetch the price edit config from the server and then saves it
 * locally using the OfflineOrderGuidePriceEditDisplayInfoAdapter.
 *
 * @returns {Object} - An object containing the downloadPriceEditDisplayInfo function.
 *
 * @function
 * @name useDownloadPriceEditDisplayInfo
 *
 * @example
 * const { downloadPriceEditDisplayInfo } = useDownloadPriceEditDisplayInfo();
 *
 * // Download and save the price edit config for a specific restaurant and supplier
 * downloadPriceEditDisplayInfo('restaurant-uuid', 'supplier-uuid');
 */
export const useDownloadPriceEditDisplayInfo = () => {
  const getOrderGuidePriceEditDisplayInfo = useLazyQuery<
    PriceEditing_GetPriceEditDisplayInfoQuery,
    PriceEditing_GetPriceEditDisplayInfoQueryVariables
  >(GET_ORDER_GUIDE_PRICE_EDIT_DISPLAY_INFO, {
    fetchPolicy: 'no-cache',
  });

  /**
   * Fetches and saves the price edit config for a given restaurant and supplier.
   *
   * @async
   * @param {string} restaurantUUID - The UUID of the restaurant.
   * @param {string} supplierUUID - The UUID of the supplier.
   */
  const downloadPriceEditDisplayInfo = async (
    restaurantUUID: string,
    supplierUUID: string,
  ) => {
    const variables = {
      restaurantUUID,
      supplierUUID,
    };

    const { data: orderGuidePriceEditDisplayInfoData } =
      await getOrderGuidePriceEditDisplayInfo({
        context: {
          offlineSync: true,
        },
        variables,
      });
    const offlineOrderGuidePriceEditDisplayInfoAdapter =
      new OfflineOrderGuidePriceEditDisplayInfoAdapter(
        restaurantUUID,
        supplierUUID,
      );
    await offlineOrderGuidePriceEditDisplayInfoAdapter.save(
      orderGuidePriceEditDisplayInfoData,
    );

    const backgroundSyncRegistryAdapter =
      new OfflineBackgroundSyncRegistryAdapter();
    await backgroundSyncRegistryAdapter.updateSyncMetadata(
      restaurantUUID,
      supplierUUID,
      {
        query: printQuery(GET_ORDER_GUIDE_PRICE_EDIT_DISPLAY_INFO),
        syncType: OfflineSyncType.PriceEditDisplayInfo,
        variables,
      },
    );
  };

  return { downloadPriceEditDisplayInfo };
};
